import { Button, Modal, pantryColors, pantryTypography } from '@pantry/ui'
import type React from 'react'

const p = pantryColors
const F = pantryTypography.fontFamily

export type LoyaltyProgramIntent = 'enable' | 'disable'

interface LoyaltyProgramConfirmDialogProps {
  open: boolean
  intent: LoyaltyProgramIntent
  loading: boolean
  onConfirm: () => void
  onCancel: () => void
}

// Copy mirrors the Settings → Integrations → Breadstack Loyalty confirm dialog so
// the two entry points read consistently. Both flip the same store-level flag.
const COPY: Record<
  LoyaltyProgramIntent,
  { title: string; body: string; confirm: string; confirming: string }
> = {
  enable: {
    title: 'Enable Loyalty Program',
    body: 'Enabling Breadstack Loyalty will start accruing points on completed orders based on your loyalty rules. Do you want to continue?',
    confirm: 'Enable',
    confirming: 'Enabling…',
  },
  disable: {
    title: 'Disable Loyalty Program',
    body: 'Disabling Breadstack Loyalty will stop points from accruing on new orders. Existing balances and rules will be preserved. Do you want to continue?',
    confirm: 'Disable',
    confirming: 'Disabling…',
  },
}

/**
 * Confirmation step for turning the store's loyalty program on/off from the
 * Points tab. Presentational — the parent owns the pending intent and the actual
 * toggle mutation (see RulesTab + useBsLoyaltyToggle).
 */
export const LoyaltyProgramConfirmDialog: React.FC<
  LoyaltyProgramConfirmDialogProps
> = ({ open, intent, loading, onConfirm, onCancel }) => {
  const copy = COPY[intent]

  const footer = (
    <div style={{ display: 'flex', justifyContent: 'flex-end', gap: 8 }}>
      <Button variant="ghost" onClick={onCancel} disabled={loading}>
        Cancel
      </Button>
      <Button variant="primary" onClick={onConfirm} disabled={loading}>
        {loading ? copy.confirming : copy.confirm}
      </Button>
    </div>
  )

  return (
    <Modal
      open={open}
      onClose={loading ? () => {} : onCancel}
      title={copy.title}
      width={360}
      footer={footer}
      zIndex={1400}
    >
      <div
        style={{
          fontFamily: F,
          fontSize: 14,
          lineHeight: 1.5,
          color: p.gray[600],
        }}
      >
        {copy.body}
      </div>
    </Modal>
  )
}
